import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Moon, Sun, Clock, Send, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function MyShift() {
  const [shiftStatus, setShiftStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);
  const [form, setForm] = useState({
    shift_type: 'night',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => { fetchShiftStatus(); }, []);

  const fetchShiftStatus = async () => {
    try {
      const res = await api.get('/employee/shift-status');
      setShiftStatus(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, end_date: form.end_date || null };
      await api.post('/employee/shift-request', payload);
      toast.success('Demande de shift soumise');
      setRequestOpen(false);
      setForm({ shift_type: 'night', start_date: '', end_date: '', reason: '' });
      fetchShiftStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getShiftLabel = (type) => type === 'night' ? 'Nuit (19h00 - 05h00)' : 'Jour (08h30 - 17h30)';
  const getShiftIcon = (type) => type === 'night' ? <Moon className="w-6 h-6 text-indigo-500" /> : <Sun className="w-6 h-6 text-amber-500" />;

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  const currentShift = shiftStatus?.current_shift || 'day';
  const activeAssignment = shiftStatus?.active_assignment;
  const pendingRequest = shiftStatus?.pending_request;

  return (
    <div className="space-y-6" data-testid="my-shift-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Mon Shift</h1>
        <p className="text-slate-600 mt-1">Consultez et demandez un changement de shift</p>
      </div>

      {/* Current Shift */}
      <Card className={`border-2 ${currentShift === 'night' ? 'border-indigo-300 bg-indigo-50/30' : 'border-amber-300 bg-amber-50/30'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-full ${currentShift === 'night' ? 'bg-indigo-100' : 'bg-amber-100'}`}>
              {getShiftIcon(currentShift)}
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Shift actuel</p>
              <p className="text-2xl font-bold text-slate-900">{getShiftLabel(currentShift)}</p>
              {activeAssignment && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-slate-500">
                    Depuis le {activeAssignment.start_date}
                    {activeAssignment.end_date ? ` jusqu'au ${activeAssignment.end_date}` : ' (permanent)'}
                  </span>
                  <Badge variant="outline" className="bg-green-100 text-green-700">Actif</Badge>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Request */}
      {pendingRequest && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="pending-shift-request">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" /> Demande en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {getShiftIcon(pendingRequest.shift_type)}
                <div>
                  <p className="font-medium">Shift {pendingRequest.shift_type === 'night' ? 'de nuit' : 'de jour'}</p>
                  <p className="text-sm text-slate-500">
                    A partir du {pendingRequest.start_date}
                    {pendingRequest.end_date ? ` jusqu'au ${pendingRequest.end_date}` : ' (permanent)'}
                  </p>
                  {pendingRequest.reason && (
                    <p className="text-sm text-slate-400 mt-1">Motif : {pendingRequest.reason}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">En attente d'approbation</Badge>
                <span className="text-xs text-slate-400">
                  Soumise le {new Date(pendingRequest.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Request Button */}
      {!pendingRequest && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-indigo-500" /> Demander un changement de shift
            </CardTitle>
            <CardDescription>
              Votre demande sera soumise pour approbation par le RH et le Directeur
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setRequestOpen(true)} className="w-full md:w-auto" data-testid="request-shift-btn">
              <Send className="w-4 h-4 mr-2" /> Faire une demande de shift nuit
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card className="border-slate-200 bg-slate-50/50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-700 mb-2">Horaires des shifts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-200">
              <Sun className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium text-slate-800">Shift de jour</p>
                <p className="text-sm text-slate-500">Lundi - Vendredi : 08h30 - 17h30</p>
                <p className="text-sm text-slate-500">Samedi : 08h00 - 12h00</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-indigo-200">
              <Moon className="w-5 h-5 text-indigo-500" />
              <div>
                <p className="font-medium text-slate-800">Shift de nuit</p>
                <p className="text-sm text-slate-500">19h00 - 05h00</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Request Modal */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-indigo-500" /> Demande de shift
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRequest} className="space-y-4">
            <div className="space-y-2">
              <Label>Type de shift *</Label>
              <select
                value={form.shift_type}
                onChange={(e) => setForm({ ...form, shift_type: e.target.value })}
                className="w-full rounded-md border border-slate-300 p-2"
                data-testid="shift-type-select"
              >
                <option value="night">Nuit (19h00 - 05h00)</option>
                <option value="day">Jour (08h30 - 17h30)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de debut *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  required
                  data-testid="shift-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="shift-end-date"
                />
                <p className="text-xs text-slate-500">Vide = permanent</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Motif de la demande *</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                required
                placeholder="Expliquez la raison de votre demande..."
                rows={3}
                data-testid="shift-reason"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setRequestOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1" data-testid="shift-submit-btn">
                <Send className="w-4 h-4 mr-2" /> Soumettre
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
